import { EventClickArg, EventContentArg } from '@fullcalendar/core';
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import timegridPlugin from '@fullcalendar/timegrid';
import { CheckCircle, CorporateFare, Public, SyncAlt, Tour } from '@mui/icons-material';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import ExtensionIcon from '@mui/icons-material/Extension';
import CircleIcon from '@mui/icons-material/Circle';
import Home from '@mui/icons-material/Home';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StarRate from '@mui/icons-material/StarRate';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import SearchIcon from '@mui/icons-material/Search';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import {
  AlertColor,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import {
  buildCalendarVenueResources,
  findVenueResourceById,
  isParentVenueBooking,
} from './BookingsActions';
import VenueAvailabilityGrid, { VenueFilterSelect } from './VenueAvailabilityGrid';
import moment from 'moment';
import 'moment/min/locales';
import React, { FC, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Views } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import strings from '../../../common/Translation/Translate';
import DateInput from '../../../components/DateInput/DateInput';
import DrawerMenu from '../../../components/DrawerMenu';
import LegendInfo from '../../../components/LegendInfo';
import Notification from '../../../components/Notification';
import PageLoader from '../../../components/PageLoader';
import axiosInstance from '../../../utils/axios';
import { identifiers } from '../../../utils/constants/identifiers';
import { colors } from '../../../utils/constants/data';
import { routes } from '../../../utils/constants/routes';
import { URLS } from '../../../utils/constants/urls';
import GetQuery from '../../../utils/hooks/getQuery';
import {
  defaultLanguage,
  Privilege,
  ShowSideBar,
} from '../../../utils/redux/reducer/authentication-slice';
import { initialDateForCalendar, setInitialDate } from '../../../utils/redux/reducer/booking-slice';
import { fetchFromStorage, saveToStorage } from '../../../utils/storage';
import {
  downloadExportFile,
  getDrawerWidth,
} from '../../../utils/validators/HelperFunctions';
import PrimaryButton from '../../../components/Button/PrimaryButton';
import BookingTableInfo from './BookingTableInfo';
import BookingMonthYearPicker from './BookingMonthYearPicker';
import { buildNewBookingUrl, NewBookingUrlParams } from './bookingCalendarActions';
import './bookingsStyle.scss';
import { privileges } from '../../../utils/constants/privileges';
import BookingTooltip from '../../../components/BookingTooltip';
import { formatGuestCountCompact } from '../../../utils/validators/HelperFunctions';

const WAITLISTED = 'WAITLISTED';
const TENTATIVE = 'TENTATIVE';
const CONFIRMED = 'CONFIRMED';
const VENUE_AVAILABILITY = 'venueAvailability';
const ENQUIRY = 'ENQUIRY';
const AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION';
const AWAITING_CONFIRMATION_COLOR = '#A3A329';

const CANCELLED = 'CANCELLED';
const CANCELLATION_REQUEST = 'CANCELLATION_REQUEST';
const UNAVAILABLE = 'UNAVAILABLE';
const HIGH_DEMAND = 'HIGH_DEMAND';
const CALENDAR_OVERLAY_LEGEND_STATUSES = [HIGH_DEMAND, UNAVAILABLE];
const ACTUALIZED = 'ACTUALIZED';
const BLOCKED = 'BLOCKED';
const EXPIRED = 'EXPIRED';

const BOOKING_CALENDAR_CHIP_TITLE_SESSION_KEY = 'bookingCalendarChipTitleMode';

const toSentenceCase = (label: string) => {
  const normalized = String(label || '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim();
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

type CalendarChipTitleMode = 'placeOrVenue' | 'companyOrCustomer';

const resolveCalendarChipTitle = (
  mode: CalendarChipTitleMode,
  args: {
    companyName?: string;
    customerName?: string;
    defaultTitle: string;
  },
): string => {
  const fallback = (args.defaultTitle || '').trim();
  if (mode === 'companyOrCustomer') {
    const company = (args.companyName || '').trim();
    const customer = (args.customerName || '').trim();
    return company || customer || fallback;
  }
  return fallback;
};

interface BookingsProps {
  modal?: boolean;
  hideNewBookingButton?: boolean;
  newBookingParams?: NewBookingUrlParams;
}

export const Bookings: FC<BookingsProps> = ({ modal, hideNewBookingButton = false, newBookingParams }) => {
  const userPrivileges = useSelector(Privilege);
  const canExportAlif = userPrivileges.includes(privileges.export_alif);
  const theme = useTheme();
  const screenSizeDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const screenSizeDownBookingsMobile = useMediaQuery('(max-width:768px)');
  const screenSizeDownMd = useMediaQuery(theme.breakpoints.down('md'));
  let calendarRef: RefObject<any> = useRef();
  const dayScrollKeyRef = useRef('');
  let calendarApi = calendarRef.current?.getApi();
  const showSideBar = useSelector(ShowSideBar);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const query = GetQuery('type' as any);

  const selectedLanguage = useSelector(defaultLanguage);
  const initialDate = useSelector(initialDateForCalendar);
  const [currentDate, setCurrentDate] = useState<any>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [partOfDays, setPartOfDays] = useState<{ code: string; name: string }[]>([]);
  const [viewType, setViewType] = useState<any>(
    query === Views.MONTH || query === Views.WEEK || query === Views.DAY || query === Views.AGENDA || query === VENUE_AVAILABILITY
      ? query
      : Views.MONTH,
  );
  const [modalDates, setModalDates] = useState<any>([]);
  const [isClose, setIsClose] = useState<boolean>(false);
  const [focusSection, setFocusSection] = useState<'unavailable' | 'highDemand' | undefined>(undefined);
  const [bookingsData, setBookingsData] = useState<any>([]);
  const [venueList, setVenueList] = useState<any>([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [isExportReport, setIsExportReport] = useState<boolean>(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exportDate, setExportDate] = useState<any>(dayjs().format('YYYY-MM-DD'));
  const [isLegendOpen, setIsLegendOpen] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [calendarChipTitleMode, setCalendarChipTitleMode] = useState<CalendarChipTitleMode>(() => {
    const stored = fetchFromStorage(BOOKING_CALENDAR_CHIP_TITLE_SESSION_KEY, true);
    return stored === 'companyOrCustomer' ? 'companyOrCustomer' : 'placeOrVenue';
  });
  const calendarLocale = selectedLanguage?.value ? selectedLanguage.value.split('_')[0].toLowerCase() : 'en';
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const [formData, setFormData] = useState<any>(() => {
    try {
      const raw = fetchFromStorage(identifiers.filters);
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return { includeCancelled: Boolean(parsed?.includeCancelled) };
    } catch {
      return { includeCancelled: false };
    }
  });
  const [diarySearch, setDiarySearch] = useState('');
  const skipSearchFetchRef = useRef(true);

  const calendarFlag = (strings: any) => [
    { label: toSentenceCase(strings.tentativeText), status: TENTATIVE, class: 'text-warning', color: 'orange' },
    { label: toSentenceCase(strings.waitListText), status: WAITLISTED, class: 'text-primary', color: '#007bff' },
    { label: toSentenceCase(strings.confirmedText), status: CONFIRMED, class: 'text-success', color: 'green' },
    { label: toSentenceCase('Enquiry'), status: ENQUIRY, class: 'enquiry', color: '#5C6BC0' },
    {
      label: toSentenceCase(strings.awaitingConfirmationText),
      status: AWAITING_CONFIRMATION,
      class: 'awaitingConfirmation',
      color: AWAITING_CONFIRMATION_COLOR,
    },
    { label: toSentenceCase(strings.actualization_text), status: ACTUALIZED, class: 'actualizationIcon', color: '#800080' },
    { label: toSentenceCase(strings.cancelledText), status: CANCELLED, class: 'text-danger', color: 'red' },
    {
      label: toSentenceCase(strings.cancelRequestText),
      status: CANCELLATION_REQUEST,
      class: 'text-info',
      color: colors.CANCELLATION_REQUEST,
    },
    { label: toSentenceCase(strings.expired_text), status: EXPIRED, class: 'expired', color: '#F4A6A6' },
    { label: toSentenceCase(strings.highDemand_text), status: HIGH_DEMAND, class: 'highDemandIcon', color: '#f85f18' },
    { label: toSentenceCase(strings.unavailable_text), status: UNAVAILABLE, class: 'unavailableIcon', color: '#ffa9b0' },
    { label: toSentenceCase(strings.blocked_text), status: BLOCKED, class: 'blocked', color: '#9e9e9e' },
  ];

  useEffect(() => {
    setTimeout(() => calendarApi?.updateSize(), 300);
  }, [showSideBar, calendarApi]);

  useEffect(() => {
    fetchVenues();
    const loadPartOfDays = async () => {
      try {
        const { data } = await axiosInstance.get(URLS.getPartOfDays);
        setPartOfDays(
          (data ?? []).map((item: { id: string | number; name: string }) => ({
            code: String(item.id),
            name: item.name,
          })),
        );
      } catch {
        setPartOfDays([]);
      }
    };
    loadPartOfDays();
  }, []);

  useEffect(() => {
    if (venueList.length > 0) {
      fetchFilterValue(initialDate || new Date(), viewType);
    }
  }, [viewType, initialDate, venueList]);

  const skipVenueFetchRef = useRef(true);
  useEffect(() => {
    if (skipVenueFetchRef.current) {
      skipVenueFetchRef.current = false;
      return;
    }
    if (!venueList.length) {
      return;
    }
    getBookings(
      calendarApi?.getDate() ?? initialDate ?? new Date(),
      formData,
      false,
      viewType,
      selectedStatus || undefined,
    );
  }, [selectedVenueId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (skipSearchFetchRef.current && !diarySearch.trim()) {
        skipSearchFetchRef.current = false;
        return;
      }
      skipSearchFetchRef.current = false;
      if (!venueList.length) {
        return;
      }
      getBookings(
        calendarApi?.getDate() ?? initialDate ?? new Date(),
        formData,
        true,
        viewType,
        selectedStatus || undefined,
      );
    }, 350);
    return () => clearTimeout(timer);
  }, [diarySearch]);

  useEffect(() => {
    updateLanguage();
  }, [selectedLanguage]);

  useEffect(() => {
    if (!modal) {
      navigate(`${routes.bookings}?type=${viewType}`);
    }
  }, [navigate, viewType, modal]);

  const fetchVenues = async () => {
    try {
      setIsLoading(true);
      const { status, data } = await axiosInstance.get(URLS.venues);
      if (status === 200) {
        setIsLoading(false);
        if (data.length > 0) {
          setVenueList(buildCalendarVenueResources(data));
        }
      }
    } catch (e) {
      setIsLoading(false);
    }
  };
  const getFirstAndLastDays = (newDate: any) => {
    const date = new Date(newDate);
    if (viewType === Views.WEEK) {
      const weekDayNumber1 = moment(newDate).day(0);
      const weekDayNumber2 = moment(newDate).day(6);
      return {
        startDate: weekDayNumber1.format('YYYY-MM-DD'),
        endDate: weekDayNumber2.format('YYYY-MM-DD'),
      };
    } else if (viewType === Views.DAY) {
      return {
        startDate: moment(date).format('YYYY-MM-DD'),
        endDate: moment(date).format('YYYY-MM-DD'),
      };
    } else {
      return {
        startDate: new Date(date.getFullYear(), date.getMonth(), 1),
        endDate: new Date(date.getFullYear(), date.getMonth() + 1, 0),
      };
    }
  };

  const bookingOverlapsVisibleRange = (booking: any, visibleStart: string | Date, visibleEnd: string | Date) => {
    if (!booking?.start || !booking?.end) {
      return false;
    }
    const start = dayjs(booking.start).startOf('day');
    const end = dayjs(booking.end).startOf('day');
    const rangeStart = dayjs(visibleStart).startOf('day');
    const rangeEnd = dayjs(visibleEnd).startOf('day');
    return !start.isAfter(rangeEnd, 'day') && !end.isBefore(rangeStart, 'day');
  };

  const visibleOverlayStatuses = useMemo(() => {
    const calendarViewDate = initialDate || calendarApi?.getDate() || new Date();
    const { startDate, endDate } = getFirstAndLastDays(calendarViewDate);

    return CALENDAR_OVERLAY_LEGEND_STATUSES.filter(status =>
      bookingsData.some(
        (booking: any) => booking.status === status && bookingOverlapsVisibleRange(booking, startDate, endDate),
      ),
    );
  }, [bookingsData, initialDate, viewType]);

  const statusCounts = useMemo(() => {
    const calendarViewDate = initialDate || calendarApi?.getDate() || new Date();
    const { startDate, endDate } = getFirstAndLastDays(calendarViewDate);
    const counts: Record<string, number> = {};

    bookingsData.forEach((booking: any) => {
      if (!booking?.status || !bookingOverlapsVisibleRange(booking, startDate, endDate)) {
        return;
      }
      counts[booking.status] = (counts[booking.status] || 0) + 1;
    });

    return counts;
  }, [bookingsData, initialDate, viewType]);

  const createBodyData = (_body: any, _filterData: any, statusFilter?: string) => {
    const filters: string[] = [];
    const trimmedSearch = String(diarySearch || '')
      .trim()
      .replace(/:/g, ' ');
    if (trimmedSearch) {
      filters.push(`search:cic:${trimmedSearch}`);
    }
    if (statusFilter) {
      if (filters.length > 0) {
        filters.push('and');
      }
      filters.push(`status:cic:${statusFilter}`);
    }
    const venueScopedView =
      viewType === Views.WEEK || viewType === Views.DAY || viewType === VENUE_AVAILABILITY;
    if (venueScopedView && selectedVenueId) {
      if (filters.length > 0) {
        filters.push('and');
      }
      filters.push(`venueId:cic:${selectedVenueId}`);
    }
    const bodyData = {
      page: 1,
      size: 10000,
      filter: filters,
      sort: [],
    };
    return bodyData;
  };

  const createQueryString = (dates: any, filterData: any) => {
    const queryStringData = {
      endDate: dates?.endDate ? moment(dates.endDate).format('YYYY-MM-DD') : null,
      startDate: dates?.startDate ? moment(dates.startDate).format('YYYY-MM-DD') : null,
      includeCancelled: filterData.includeCancelled || false,
    };
    const overlap = viewType === VENUE_AVAILABILITY ? '&overlap=true' : '';
    return dates?.startDate
      ? `startDate=${queryStringData.startDate}&endDate=${queryStringData.endDate}&showCancelled=${queryStringData.includeCancelled}${overlap}`
      : `showCancelled=${queryStringData.includeCancelled}${overlap}`;
  };
  const crypto = window.crypto;
  const array = new Uint32Array(1);

  const getBookings = async (date: any, body: any, isClear: boolean, viewType: string, statusFilter?: string) => {
    closeNotification(false);
    if (typeof body !== 'object') {
      JSON.parse(body);
    }
    const dates = getFirstAndLastDays(date);
    const filterData = typeof body !== 'object' ? JSON.parse(body) : body;
    setIsLoading(true);
    try {
      const bodyData = createBodyData(body, filterData, statusFilter);
      const queryString = createQueryString(date ? dates : null, filterData);
      const { status, data } = await axiosInstance.post(`${URLS.bookings}?${queryString}`, bodyData);

      const createBlockedEventId = (seed: number) => parseInt(String(seed) + crypto.getRandomValues(array), 10);

      const modifiedForParentChild = data.content
        .filter(
          (item: any) => !(item.status === 'UNAVAILABLE' || item.status === 'HIGH_DEMAND' || item.status === BLOCKED),
        )
        .flatMap((item: any) => {
          const blockedStart = moment(item.start).subtract(item.preLeadTime ?? 0, 'minutes');
          const blockedEnd = moment(item.end).add(item.postLeadTime ?? 0, 'minutes');

          if (isParentVenueBooking(item)) {
            const parentVenue = findVenueResourceById(venueList, item.venueId);
            if (!parentVenue?.children?.length) {
              return [];
            }
            return parentVenue.children.map((child: any) => ({
              id: createBlockedEventId(child.id),
              venueId: child.id,
              title: child.title,
              status: BLOCKED,
              start: blockedStart,
              end: blockedEnd,
              allDay: item.allDay,
              resourceId: child.id,
            }));
          }

          const parentVenue = findVenueResourceById(venueList, item.parentId);
          if (!parentVenue) {
            return [];
          }
          return [
            {
              id: createBlockedEventId(parentVenue.id),
              venueId: parentVenue.id,
              title: parentVenue.title,
              start: blockedStart,
              end: blockedEnd,
              allDay: item.allDay,
              status: BLOCKED,
              resourceId: parentVenue.id,
            },
          ];
        });
      const dataWithNewBlockedId = data.content.map((item: any) =>
        item.status === BLOCKED ? { ...item, id: parseInt(item.id + crypto.getRandomValues(array)) } : item,
      );
      let modifiedData =
        viewType === Views.MONTH || viewType === VENUE_AVAILABILITY
          ? [...data.content]
          : [...dataWithNewBlockedId, ...modifiedForParentChild];

      console.log(
        'modifiedData: ',
        modifiedData
          .filter((item: any) => !(item.status === 'UNAVAILABLE' || item.status === 'HIGH_DEMAND'))
          .map((i: any) => ({ ...i, start: moment(i.start), end: moment(i.end) })),
      );

      if (status === 200) {
        setIsClose(false);
        setIsLoading(false);
        let bookingArray = [];
        if (modifiedData.length) {
          bookingArray = modifiedData.map((booking: any) => {
            return {
              ...booking,
              title: booking.placeOfEvent ? booking.placeOfEvent : booking.title,
              start: new Date(moment.utc(booking.start).local() as any),
              end: new Date(moment.utc(booking.end).local() as any),
            };
          });
        }

        setBookingsData(bookingArray);

        if (!statusFilter) {
          const uniqueStatuses = Array.from(
            new Set(
              modifiedData
                .filter((i: any) => !(i.status === UNAVAILABLE || i.status === HIGH_DEMAND || i.status === BLOCKED))
                .map((i: any) => i.status),
            ),
          ) as string[];
          setAvailableStatuses(uniqueStatuses);
        }
      }
    } catch (e: any) {
      setIsLoading(false);
      if (e.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: e.response?.data.message,
        });
      }
    }
  };
  const fetchFilterValue = (dates: any, viewType: string) => {
    setSelectedStatus('');
    getBookings(dates, formData, true, viewType);
  };

  const handleCalendarChipTitleModeChange = (_: React.MouseEvent<HTMLElement>, value: CalendarChipTitleMode | null) => {
    if (value == null) return;
    setCalendarChipTitleMode(value);
    saveToStorage(BOOKING_CALENDAR_CHIP_TITLE_SESSION_KEY, value, true);
  };

  const handleLegendClick = (status: string) => {
    const newStatus = selectedStatus === status ? '' : status;
    setSelectedStatus(newStatus);
    const api = calendarRef.current?.getApi();
    getBookings(api?.getDate() ?? initialDate ?? new Date(), formData, false, viewType, newStatus || undefined);
  };

  const getViewedMonthRange = () => {
    const viewDate = initialDate || calendarApi?.getDate() || new Date();
    return {
      startDate: dayjs(viewDate).startOf('month').format('DD-MM-YYYY'),
      endDate: dayjs(viewDate).endOf('month').format('DD-MM-YYYY'),
    };
  };

  const downloadBlobExport = async (request: Promise<any>, fallbackName: string) => {
    setExportMenuAnchor(null);
    setIsLoading(true);
    try {
      const { status, data, headers } = await request;
      if (status === 200) {
        const match = RegExp(/filename="?(.+?)"?$/).exec(headers['content-disposition'] ?? '');
        downloadExportFile(data, 'xlsx', match?.[1] || fallbackName);
      }
    } catch (error: any) {
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          setMessage({ display: true, severity: 'error', message: json.message });
        } catch {
          setMessage({ display: true, severity: 'error', message: text || 'Failed to export report' });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportMonthSummary = () =>
    downloadBlobExport(
      axiosInstance.post(URLS.exportReport, getViewedMonthRange(), { responseType: 'blob' }),
      'Export',
    );

  const handleExportSettlementReport = () =>
    downloadBlobExport(
      axiosInstance.post(URLS.exportSettlementReport, getViewedMonthRange(), { responseType: 'blob' }),
      'Settlement_Report',
    );

  const handleExportCoverReport = () => {
    const viewDate = initialDate || calendarApi?.getDate() || new Date();
    const referenceDate = dayjs(viewDate).format('YYYY-MM-DD');
    return downloadBlobExport(
      axiosInstance.get(`${URLS.exportCoverReport}?referenceDate=${referenceDate}`, { responseType: 'blob' }),
      'Cover_Report',
    );
  };

  const handleExportReport = async () => {
    setIsLoading(true);
    try {
      const { status, data, headers } = await axiosInstance.get(`${URLS.exportBookings}?date=${exportDate}`, {
        responseType: 'blob',
      });
      if (status === 200) {
        setIsLoading(false);
        setIsExportReport(false);
        const match = RegExp(/filename="?(.+?)"?$/).exec(headers['content-disposition']);
        let filename = 'Export';
        if (match) {
          filename = match[1];
        }
        downloadExportFile(data, 'xlsx', filename);
      }
    } catch (error: any) {
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text(); // convert blob -> text
        const json = JSON.parse(text);
        setMessage({ display: true, severity: 'error', message: json.message });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAlifExport = async (type: 'rv' | 'cl') => {
    if (!canExportAlif) {
      return;
    }
    setIsLoading(true);
    try {
      const url = type === 'rv' ? URLS.exportAlifRv : URLS.exportAlifCl;
      const { status, data, headers } = await axiosInstance.get(`${url}?date=${exportDate}`, {
        responseType: 'blob',
      });
      if (status === 200) {
        const match = RegExp(/filename="?(.+?)"?$/).exec(headers['content-disposition']);
        let filename = type === 'rv' ? 'ALIF_RV' : 'ALIF_CL';
        if (match) {
          filename = match[1];
        }
        downloadExportFile(data, 'txt', filename);
      }
    } catch (error: any) {
      if (error.response && error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          setMessage({ display: true, severity: 'error', message: json.message });
        } catch {
          setMessage({ display: true, severity: 'error', message: text || 'Failed to export ALIF file' });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const cellDrawerGuard = useRef(0);
  const openCellDrawer = (date: Date, clickedStatus?: string) => {
    const now = Date.now();
    if (now - cellDrawerGuard.current < 400) return;
    cellDrawerGuard.current = now;
    const selectedDate = moment(date).format('DD-MM-YYYY');
    setIsLoading(true);
    handleSelectSlotHelper(
      selectedDate,
      { selectedDate, monthEndDate: moment(date).endOf('month').format('DD-MM-YYYY') },
      clickedStatus,
    );
  };
  const cellClickStatus = (date: Date, resourceId?: string | number) => {
    const venueUnavailable =
      resourceId != null &&
      bookingsData.some(
        (booking: any) =>
          booking.status === UNAVAILABLE &&
          Number(booking.venueId) === Number(resourceId) &&
          isDateInRange(date, booking.start, booking.end),
      );
    if (venueUnavailable) return UNAVAILABLE;
    const highDemand = bookingsData.some(
      (booking: any) => booking.status === HIGH_DEMAND && isDateInRange(date, booking.start, booking.end),
    );
    return highDemand ? HIGH_DEMAND : undefined;
  };

  const handleNavigateToBookingDetail = (clickInfo: EventClickArg) => {
    const { status } = clickInfo.event.extendedProps;
    if (clickInfo.event.id != null && status !== BLOCKED && status !== UNAVAILABLE) {
      navigate(routes.bookings + '/' + clickInfo.event.id);
    }
  };
  const handleSelectSlotHelper = async (newDates: any, senData: any, clickedStatus?: string) => {
    try {
      const { status, data } = await axiosInstance.post(URLS.venuesAvailability, senData);

      if (status === 200) {
        setIsLoading(false);
        const modalDates = {
          unavailableDates: data.unavailableVenues || [],
          highDemandDates: data.highDemandingVenues || [],
        };
        setIsClose(true);
        if (clickedStatus === UNAVAILABLE) {
          setFocusSection('unavailable');
        } else if (clickedStatus === HIGH_DEMAND) {
          setFocusSection('highDemand');
        } else {
          setFocusSection(undefined);
        }
        setModalDates(modalDates);
        setCurrentDate(newDates);
      }
      return;
    } catch (err) {
      setIsLoading(false);
    }
  };
  const handleSelectSlot = (selectInfo: any) => {
    if (viewType === Views.DAY || viewType === Views.WEEK) {
      selectInfo.view?.calendar?.unselect();
      openCellDrawer(selectInfo.start, cellClickStatus(selectInfo.start, selectInfo.resource?.id));
      return false;
    }
    if (viewType === Views.MONTH) {
      const { start } = selectInfo;
      const dates = moment(start).format('DD MMMM YYYY');
      const newDates = moment(start).format('DD-MM-YYYY');
      const newDate =
        bookingsData && bookingsData.filter((item: any) => item.status === UNAVAILABLE || item.status === HIGH_DEMAND);
      if (newDate) {
        for (const element of newDate) {
          const start = moment(element.start).format('DD MMMM YYYY');
          const end = moment(element.end).format('DD MMMM YYYY');
          const dateFormat = moment(dates).isSameOrBefore(end) && moment(dates).isSameOrAfter(start);
          if (dateFormat && element.externalBooking !== true) {
            const dates = getFirstAndLastDays(calendarApi?.getDate());
            const newEndDates = moment(dates.endDate).format('DD-MM-YYYY');
            const senData = { selectedDate: newDates, monthEndDate: newEndDates };
            setIsLoading(true);
            handleSelectSlotHelper(newDates, senData);
            return false;
          }
        }
      }
    }
  };
  const handleDateNavigate = (type: any) => () => {
    if (viewType === VENUE_AVAILABILITY) {
      const next = moment(initialDate || new Date()).add(type === 'add' ? 1 : -1, 'month');
      dispatch(setInitialDate(next.format('YYYY-MM-DD')));
      return;
    }
    if (type === 'add') {
      calendarApi.next();
    } else {
      calendarApi.prev();
    }
    dispatch(setInitialDate(moment(calendarApi?.getDate()).format('YYYY-MM-DD')));
  };
  const isViewingToday = () => {
    const date = calendarApi?.getDate() || initialDate;
    if (!date) return false;
    if (viewType === Views.MONTH || viewType === Views.AGENDA || viewType === VENUE_AVAILABILITY) {
      return moment(date).isSame(moment(), 'month');
    }
    if (viewType === Views.WEEK) {
      return moment().isBetween(moment(date).day(0), moment(date).day(6), 'day', '[]');
    }
    return moment(date).isSame(moment(), 'day');
  };
  const setToday = () => {
    if (viewType === VENUE_AVAILABILITY) {
      dispatch(setInitialDate(moment().format('YYYY-MM-DD')));
      return;
    }
    calendarApi.today();
    dispatch(setInitialDate(moment(calendarApi?.getDate()).format('YYYY-MM-DD')));
  };
  const onMonthChange = (value: Dayjs) => {
    if (!dayjs(value).isValid()) {
      return;
    }
    if (viewType === VENUE_AVAILABILITY) {
      dispatch(setInitialDate(value.startOf('month').format('YYYY-MM-DD')));
      return;
    }
    calendarApi.gotoDate(value.startOf('month').toDate());
    dispatch(setInitialDate(moment(calendarApi?.getDate()).format('YYYY-MM-DD')));
  };
  const onDayChange = (value: Dayjs) => {
    if (!dayjs(value).isValid()) {
      return;
    }
    const date = value.startOf('day');
    if (calendarApi) {
      calendarApi.gotoDate(date.toDate());
      dispatch(setInitialDate(moment(calendarApi.getDate()).format('YYYY-MM-DD')));
      return;
    }
    dispatch(setInitialDate(date.format('YYYY-MM-DD')));
  };
  const renderCompactDateNav = () => {
    const newDate = initialDate || calendarApi?.getDate();
    const locale = selectedLanguage?.value?.split('_')[0] || 'en';
    let center: React.ReactNode = null;
    if (viewType === Views.MONTH || viewType === Views.AGENDA || viewType === VENUE_AVAILABILITY) {
      center = (
        <BookingMonthYearPicker
          value={newDate}
          locale={locale}
          onChange={onMonthChange}
        />
      );
    } else if (viewType === Views.WEEK) {
      const weekStart = moment(newDate).locale(locale).day(0);
      const weekEnd = moment(newDate).locale(locale).day(6);
      center = (
        <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', whiteSpace: 'nowrap', px: 0.5 }}>
          {weekStart.format('D MMM')} – {weekEnd.format('D MMM YYYY')}
        </Typography>
      );
    } else {
      center = (
        <BookingMonthYearPicker
          value={newDate}
          locale={locale}
          onChange={onDayChange}
          selectDay
        />
      );
    }
    return (
      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={0} sx={{ flexShrink: 0 }}>
          <IconButton size="small" onClick={handleDateNavigate('subtract')} aria-label={strings.prev_text}>
            <ChevronLeftIcon />
          </IconButton>
          {center}
          <IconButton size="small" onClick={handleDateNavigate('add')} aria-label={strings.next_text}>
            <ChevronRightIcon />
          </IconButton>
        </Stack>
        {!isViewingToday() && (
          <Button
            size="small"
            variant="outlined"
            onClick={setToday}
            sx={{
              position: 'absolute',
              left: '100%',
              ml: 0.75,
              whiteSpace: 'nowrap',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.8125rem',
              minHeight: 28,
              px: 1.25,
              color: '#334155',
              borderColor: '#D0D5DD',
              '&:hover': {
                borderColor: '#98A2B3',
                bgcolor: '#F8FAFC',
              },
            }}>
            {strings.todayText}
          </Button>
        )}
      </Box>
    );
  };

  const changeViewType = (nextView: string) => () => {
    if (nextView !== VENUE_AVAILABILITY && calendarApi) {
      if (nextView === Views.DAY) calendarApi.changeView('resourceTimelineDay');
      else if (nextView === Views.MONTH) calendarApi.changeView('dayGridMonth');
      else if (nextView === Views.WEEK) calendarApi.changeView('resourceTimelineWeek');
    }
    setViewType(nextView);
  };
  const updateLanguage = () => {
    const selectedValue = selectedLanguage?.value;
    if (selectedValue) {
      const lang = selectedValue.split('_')[0];
      moment.locale(lang || 'en');
    }
  };
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const resolveCalendarEventColor = (status: string) =>
    calendarFlag(strings).find((i: any) => i.status === status)?.color ??
    (colors as Record<string, string>)[status];

  function renderEventContent(eventInfo: EventContentArg) {
    const { title, extendedProps, id } = eventInfo.event;
    const {
      eventType,
      eventSubType,
      eventSubtype,
      numberOfGuests,
      expectedGuests,
      guaranteedGuests,
      customerName,
      customerPhone,
      status,
      customerBooking,
      corporateBooking,
      isCorporateBooking,
      roomsAdded,
      beoReleased,
      verified,
      companyName,
      placeOfEvent,
      venueName,
      layoutName,
      pricePackageName,
      revenue,
      createdAt,
      createdBy,
      createdByName,
      salesPerson,
      externalBooking,
      idsBookingRef,
      programmingEvent,
      isProgrammingEvent,
    } = extendedProps;
    const resolvedEventSubType = eventSubType || eventSubtype;
    const resolvedCorporateBooking = Boolean(corporateBooking ?? isCorporateBooking);
    const isProgrammingEventBooking = Boolean(programmingEvent ?? isProgrammingEvent);
    const eventColor = resolveCalendarEventColor(status);
    const chipDisplayTitle = resolveCalendarChipTitle(calendarChipTitleMode, {
      companyName,
      customerName,
      defaultTitle: (title || '') as string,
    });
    const wrapWithTooltip = (children: React.ReactElement) => (
      <BookingTooltip
        eventType={eventType}
        eventSubType={resolvedEventSubType}
        numberOfGuests={numberOfGuests}
        expectedGuests={expectedGuests}
        guaranteedGuests={guaranteedGuests}
        customerName={customerName}
        customerPhone={customerPhone}
        companyName={companyName}
        venueName={venueName}
        placeOfEvent={placeOfEvent}
        layoutName={layoutName}
        pricePackageName={pricePackageName}
        revenue={revenue}
        createdByName={createdByName || createdBy}
        createdAt={createdAt}
        salesPerson={salesPerson}
        status={status}
        statusColor={eventColor}
        customerBooking={customerBooking}
        corporateBooking={resolvedCorporateBooking}
        roomsAdded={roomsAdded}
        beoReleased={beoReleased}
        externalBooking={externalBooking}
        idsBookingRef={idsBookingRef}
        startDate={eventInfo.event.start || undefined}
        endDate={eventInfo.event.end || undefined}
        programmingEvent={isProgrammingEventBooking}
        placement="top-start"
        onViewDetails={() => {
          if (id != null && status !== BLOCKED) {
            navigate(routes.bookings + '/' + id);
          }
        }}>
        {children}
      </BookingTooltip>
    );
    if (eventInfo.view.type === 'resourceTimelineWeek' || eventInfo.view.type === 'resourceTimelineDay') {
      if (status === BLOCKED) {
        return (
          <Box
            sx={{
              height: '100%',
              minHeight: 40,
              width: '100%',
              borderRadius: '4px',
              cursor: 'default',
              backgroundColor: '#F2F4F7',
              backgroundImage:
                'repeating-linear-gradient(-45deg, transparent, transparent 4px, #D0D5DD 4px, #D0D5DD 5px)',
            }}
          />
        );
      }
      const eventLine = [eventType, resolvedEventSubType].filter((value: string) => String(value || '').trim()).join(' · ');
      const timelineTitle =
        calendarChipTitleMode === 'companyOrCustomer'
          ? String(companyName || customerName || eventLine || chipDisplayTitle || '').trim()
          : eventLine || chipDisplayTitle;
      const expectedCount = expectedGuests ?? numberOfGuests;
      const guestLine =
        guaranteedGuests != null || expectedCount != null
          ? formatGuestCountCompact(guaranteedGuests ?? expectedCount, expectedCount)
          : '';
      const startLabel = eventInfo.event.start ? moment(eventInfo.event.start).format('HH:mm') : '';
      const endLabel = eventInfo.event.end ? moment(eventInfo.event.end).format('HH:mm') : '';
      const timeLabel = [startLabel, endLabel].filter(Boolean).join(' - ');
      const iconColor = '#fff';
      const blockStyle = {
        backgroundColor: eventColor,
        borderLeft: `3px solid color-mix(in srgb, ${eventColor} 55%, black)`,
        color: '#fff',
      };
      return wrapWithTooltip(
        <Box
          className="p-1 details timeline-booking-block"
          sx={{
            ...blockStyle,
            borderRadius: '4px',
            minHeight: 40,
            px: 0.75,
            py: 0.25,
            overflow: 'hidden',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}>
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 0.25 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, color: iconColor }}>
                  {customerBooking && <StarRate sx={{ fontSize: 14 }} />}
                  {resolvedCorporateBooking && <CorporateFare sx={{ fontSize: 14 }} />}
                  {externalBooking && <Public sx={{ fontSize: 13 }} />}
                  {idsBookingRef && <SyncAlt sx={{ fontSize: 13 }} />}
                  {roomsAdded && <Home sx={{ fontSize: 13 }} />}
                  {beoReleased && <Tour sx={{ fontSize: 13 }} />}
                  {verified && <CheckCircle sx={{ fontSize: 13 }} />}
                  {isProgrammingEventBooking && <ExtensionIcon sx={{ fontSize: 13 }} />}
                </Box>
                <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2 }}>
                  {timelineTitle}
                </Typography>
              </Box>
              {(guestLine || timeLabel) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, color: 'rgba(255,255,255,0.92)' }}>
                  {guestLine && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                      <GroupsOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                      <Typography noWrap sx={{ fontSize: 11, fontWeight: 500, lineHeight: 1.2 }}>
                        {guestLine}
                      </Typography>
                    </Box>
                  )}
                  {timeLabel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
                      <ScheduleOutlinedIcon sx={{ fontSize: 13, flexShrink: 0 }} />
                      <Typography noWrap sx={{ fontSize: 11, fontWeight: 500, lineHeight: 1.2 }}>
                        {timeLabel}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </>
        </Box>,
      );
    } else {
      return wrapWithTooltip(
        <Box
          sx={{
            py: 0.2,
            color: '#fff',
            backgroundColor: eventColor,
            border: `1px  solid ${eventColor}`,
            borderRadius: '3px',
            minHeight: '18px',
            maxHeight: '22px',
            cursor: 'pointer',
            overflow: 'hidden',
            '&:hover': screenSizeDownMd
              ? {
                  opacity: 0.9,
                  transform: 'scale(1.02)',
                }
              : undefined,
          }}
          display={'flex'}
          alignItems={'center'}
          width={'100%'}
          pr={0.5}
          pl={0.3}>
          <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} flex="0 0 auto">
            {customerBooking && <StarRate sx={{ fontSize: '14px', color: 'white' }} />}
            {resolvedCorporateBooking && <CorporateFare sx={{ fontSize: '14px', color: 'white' }} />}
            {externalBooking && <Public sx={{ fontSize: '12px', color: 'white' }} />}
            {idsBookingRef && <SyncAlt sx={{ fontSize: '12px', color: 'white' }} />}
            {roomsAdded && <Home sx={{ fontSize: '12px', color: 'white' }} />}
            {beoReleased && <Tour sx={{ fontSize: '12px', color: 'white' }} />}
            {verified && <CheckCircle sx={{ fontSize: '12px', color: 'white' }} />}
            {isProgrammingEventBooking && <ExtensionIcon sx={{ fontSize: '12px', color: 'white' }} />}
          </Box>
          <Typography
            component="b"
            noWrap
            sx={{
              minWidth: 0,
              ml: 0.3,
              fontSize: 'inherit',
              fontWeight: 700,
              lineHeight: 1.2,
            }}>
            {chipDisplayTitle}
          </Typography>
        </Box>,
      );
    }
  }

  // Check if date falls within a date range
  const isDateInRange = (date: any, startDate: any, endDate: any) => {
    const checkDate = dayjs(date);
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return (
      (checkDate.isAfter(start, 'day') || checkDate.isSame(start, 'day')) &&
      (checkDate.isBefore(end, 'day') || checkDate.isSame(end, 'day'))
    );
  };

  const findBookingForDate = (bookings: any[], date: any) => {
    return bookings.find((b: any) => b.start && b.end && isDateInRange(date, b.start, b.end));
  };

  const canCreateBooking = userPrivileges.includes(privileges.createBooking);

  const navigateToNewBookingWithDate = useCallback(
    (date: Date) => {
      if (!canCreateBooking) {
        return;
      }
      navigate(buildNewBookingUrl(date, newBookingParams));
    },
    [canCreateBooking, navigate, newBookingParams],
  );

  const handleDayCellDidMount = useCallback(
    (info: { date: Date; el: HTMLElement }) => {
      if (!canCreateBooking || screenSizeDownBookingsMobile || viewType !== Views.MONTH) {
        return;
      }

      if (dayjs(info.date).startOf('day').isBefore(dayjs().startOf('day'))) {
        return;
      }

      if (info.el.querySelector('.booking-day-add-btn-float')) {
        return;
      }

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'booking-day-add-btn-float';
      button.setAttribute('aria-label', strings.new_booking_title);
      button.setAttribute('title', strings.new_booking_title);
      button.textContent = '+';
      button.addEventListener('click', event => {
        event.stopPropagation();
        event.preventDefault();
        navigateToNewBookingWithDate(info.date);
      });
      info.el.appendChild(button);
    },
    [canCreateBooking, navigateToNewBookingWithDate, screenSizeDownBookingsMobile, viewType],
  );

  const handleDayCellWillUnmount = useCallback((info: { el: HTMLElement }) => {
    info.el.querySelector('.booking-day-add-btn-float')?.remove();
  }, []);

  const renderStatusBorder = (
    show: boolean,
    color: string,
    offset: number,
    onClick?: () => void,
    positionRight?: boolean,
  ) => {
    if (!show) return null;

    const styles = {
      MONTH: positionRight
        ? { top: 0, right: offset, height: '10px', width: '3vw', borderRadius: '0px 0px 0px 7px', zIndex: -1 }
        : { top: 0, left: offset, height: '10px', width: '3vw', borderRadius: '0px 0px 7px 0px', zIndex: -1 },
      WEEK: { top: -4, left: -7 + offset, height: '30px', width: '120%', zIndex: -1 },
      DAY: { top: -10, left: -5 + offset, height: '10px', width: '70px' },
    };

    const style = viewType === Views.MONTH ? styles.MONTH : viewType === Views.WEEK ? styles.WEEK : styles.DAY;

    return (
      <Box
        onClick={onClick}
        sx={{
          position: 'absolute',
          ...style,
          backgroundColor: color,
          cursor: onClick ? 'pointer' : 'default',
          '&:hover': onClick ? { opacity: 0.8 } : {},
        }}
      />
    );
  };

  const A = (laneInfo: any) => {
    const currentDate = laneInfo.date;
    const unavailableDates = bookingsData.filter((b: any) => b.status === UNAVAILABLE);
    const highDemandDates = bookingsData.filter((b: any) => b.status === HIGH_DEMAND);

    const u = findBookingForDate(unavailableDates, currentDate);
    const h = findBookingForDate(highDemandDates, currentDate);

    // Handler to open drawer with status data
    const handleStatusClick = (status: string) => {
      const dates = getFirstAndLastDays(calendarApi?.getDate());
      const selectedDate = moment(currentDate).format('DD-MM-YYYY');
      const senData = { selectedDate, monthEndDate: moment(dates.endDate).format('DD-MM-YYYY') };
      handleSelectSlotHelper(selectedDate, senData, status);
    };

    const handleUnavailableClick = () => u && handleStatusClick(UNAVAILABLE);
    const handleHighDemandClick = () => h && handleStatusClick(HIGH_DEMAND);

    const isToday = Boolean(laneInfo.isToday) || moment(currentDate).isSame(moment(), 'day');
    const isOtherMonth = Boolean(laneInfo.isOther);
    const todayNumberSx = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 26,
      height: 26,
      borderRadius: '50%',
      bgcolor: 'primary.main',
      color: '#fff',
      fontWeight: 700,
      fontSize: '0.8125rem',
      lineHeight: 1,
    };

    if (viewType === Views.MONTH) {
      return (
        <Box position="relative" display="flex" justifyContent="flex-end">
          {renderStatusBorder(!!h, '#f85f18', 0, handleHighDemandClick, false)}
          {renderStatusBorder(!!u, '#fdcbcd', 0, handleUnavailableClick, true)}
          <Typography
            component="span"
            sx={
              isToday
                ? { ...todayNumberSx, mr: 0.5, mt: 0.25 }
                : { pr: 0.5, color: isOtherMonth ? '#B8BDC6' : '#000000' }
            }>
            {laneInfo.dayNumberText}
          </Typography>
        </Box>
      );
    }

    if (viewType === Views.DAY) {
      return (
        <Typography color="#000000" fontSize="14px" fontWeight="bold">
          {laneInfo.text}
        </Typography>
      );
    }

    return (
      <Box position="relative">
        {renderStatusBorder(!!h, '#f85f18', 0, handleHighDemandClick)}
        {renderStatusBorder(!!u, '#fdcbcd', 0, handleUnavailableClick)}
        <Typography color="#000000" fontSize="14px" fontWeight="bold">
          {laneInfo.text}
        </Typography>
      </Box>
    );
  };
  const venueFilterOptions = useMemo(() => {
    const options: { id: number; name: string }[] = [];
    venueList.forEach((venue: any) => {
      options.push({ id: venue.id, name: venue.title });
      (venue.children ?? []).forEach((child: any) => options.push({ id: child.id, name: child.title }));
    });
    return options;
  }, [venueList]);

  const calendarEvents = useMemo(
    () => bookingsData.filter((item: any) => item.status !== UNAVAILABLE && item.status !== HIGH_DEMAND),
    [bookingsData],
  );

  const calendarResources = useMemo(() => {
    if (!selectedVenueId || (viewType !== Views.WEEK && viewType !== Views.DAY)) {
      return venueList;
    }
    const venueId = Number(selectedVenueId);
    for (const venue of venueList) {
      if (Number(venue.id) === venueId) {
        return [{ ...venue, children: undefined }];
      }
      const child = (venue.children ?? []).find((item: any) => Number(item.id) === venueId);
      if (child) {
        return [child];
      }
    }
    return venueList;
  }, [selectedVenueId, venueList, viewType]);

  const getInitialView = (viewType: any) => {
    if (viewType === Views.MONTH) return 'dayGridMonth';
    if (viewType === Views.WEEK) return 'resourceTimelineWeek';
    return 'resourceTimelineDay';
  };

  const toggleCancelledExpired = () => {
    const next = {
      ...formData,
      includeCancelled: !formData?.includeCancelled,
    };
    setFormData(next);
    saveToStorage(identifiers.filters, JSON.stringify(next));
    getBookings(
      calendarApi?.getDate() ?? initialDate ?? new Date(),
      next,
      true,
      viewType,
      selectedStatus || undefined,
    );
  };

  const renderDiarySearchRow = () => (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
      <TextField
        size="small"
        value={diarySearch}
        onChange={e => setDiarySearch(e.target.value)}
        placeholder={strings.booking_diary_search_placeholder}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: diarySearch ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="clear search" onClick={() => setDiarySearch('')}>
                <ClearOutlinedIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          flex: 1,
          minWidth: 180,
          bgcolor: '#fff',
          '& .MuiInputBase-root': { minHeight: 36, fontSize: '0.875rem' },
        }}
      />
      <FormControlLabel
        sx={{ flex: '0 0 auto', mr: 0, ml: 0, whiteSpace: 'nowrap' }}
        control={
          <Switch
            size="small"
            checked={Boolean(formData?.includeCancelled)}
            onChange={toggleCancelledExpired}
          />
        }
        label={
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>
            {strings.booking_diary_toggle_cancelled_expired}
          </Typography>
        }
      />
    </Stack>
  );

  const hasActiveFilterChips = Boolean(diarySearch.trim());

  const activeFilterChips = (
    <>
      {diarySearch.trim() && (
        <Chip size="small" color="primary" label={diarySearch.trim()} onDelete={() => setDiarySearch('')} />
      )}
    </>
  );

  const isLegendItemVisible = (status: string) =>
    CALENDAR_OVERLAY_LEGEND_STATUSES.includes(status)
      ? visibleOverlayStatuses.includes(status)
      : availableStatuses.includes(status);

  const renderLegendPill = (item: any, layout: 'inline' | 'desktop' = 'inline') => {
    const isOverlay = CALENDAR_OVERLAY_LEGEND_STATUSES.includes(item.status);
    const isSelected = !isOverlay && selectedStatus === item.status;
    const borderColor = isSelected ? item.color : '#9e9e9e';
    const count = statusCounts[item.status] ?? 0;
    const showCount = !selectedStatus || isSelected || count > 0;
    const countLabel = isOverlay ? 'date' : 'booking';
    const countTitle = `${count} ${countLabel}${count === 1 ? '' : 's'} in this view`;

    return (
      <Typography
        display="flex"
        alignItems="center"
        key={item.class}
        fontSize="12px"
        fontWeight="bold"
        onClick={isOverlay ? undefined : () => handleLegendClick(item.status)}
        sx={{
          cursor: isOverlay ? 'default' : 'pointer',
          px: 1.25,
          py: 0.5,
          borderRadius: '16px',
          border: '1px solid',
          borderColor: isOverlay ? item.color : borderColor,
          bgcolor: 'transparent',
          color: item.color,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          ...(layout === 'desktop' ? { ml: 1 } : {}),
          ...(!isOverlay && {
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: `${item.color}1A`,
            },
          }),
        }}>
        {isSelected && <CircleIcon sx={{ mr: 0.5 }} fontSize="inherit" style={{ color: item.color }} />}
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
          <Box component="span">{item.label}</Box>
          {showCount && (
            <Box
              component="span"
              title={countTitle}
              aria-label={countTitle}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 24,
                px: 0.875,
                py: 0.125,
                borderRadius: '999px',
                bgcolor: '#f8fafc',
                color: '#334155',
                fontSize: '11px',
                fontWeight: 700,
                lineHeight: '18px',
                border: '1px solid #dbe3ea',
                letterSpacing: '0.02em',
              }}>
              {count}
            </Box>
          )}
        </Box>
      </Typography>
    );
  };

  const legendItems = calendarFlag(strings).filter((item: any) => isLegendItemVisible(item.status));
  const statusFilterPills = legendItems.map((item: any) => renderLegendPill(item));
  const statusFilterPillsDesktop = legendItems.map((item: any) => renderLegendPill(item, 'desktop'));

  const chipTitleToggleSx = {
    flexShrink: 0,
    '& .MuiToggleButton-root': {
      px: 1,
      py: 0.25,
      fontSize: '11px',
      textTransform: 'none',
      fontWeight: 500,
    },
    '& .MuiToggleButton-root.Mui-selected': {
      fontWeight: 700,
    },
  };

  const renderChipTitleToggle = (sx = {}) => {
    const timelineTitle = viewType === Views.DAY || viewType === Views.WEEK;
    return (
    <Tooltip
      title={
        timelineTitle
          ? strings.booking_diary_chip_title_toggle_tooltip_timeline
          : strings.booking_diary_chip_title_toggle_tooltip
      }
      placement="bottom-end"
      arrow
      enterDelay={600}
      disableInteractive
      PopperProps={{ sx: { pointerEvents: 'none', maxWidth: 260 } }}
      slotProps={{ tooltip: { sx: { fontSize: '0.75rem', py: 0.5, px: 1 } } }}>
      <span style={{ display: 'inline-flex' }}>
        <ToggleButtonGroup
          exclusive
          value={calendarChipTitleMode}
          onChange={handleCalendarChipTitleModeChange}
          size="small"
          aria-label={strings.booking_diary_chip_title_toggle_aria}
          sx={{ ...chipTitleToggleSx, ...sx }}>
          <ToggleButton value="placeOrVenue">
            {timelineTitle ? strings.booking_diary_chip_title_event : strings.booking_diary_chip_title_place}
          </ToggleButton>
          <ToggleButton value="companyOrCustomer">{strings.booking_diary_chip_title_company}</ToggleButton>
        </ToggleButtonGroup>
      </span>
    </Tooltip>
    );
  };

  const renderLegendButton = (sx = {}) => (
    <Button
      size="small"
      variant="outlined"
      onClick={() => setIsLegendOpen(true)}
      startIcon={<CircleIcon sx={{ fontSize: '10px !important' }} />}
      sx={{
        flexShrink: 0,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.8125rem',
        minHeight: 28,
        px: 1.25,
        ml: 0.5,
        color: '#334155',
        borderColor: '#D0D5DD',
        bgcolor: '#fff',
        '& .MuiButton-startIcon': { mr: 0.75 },
        '&:hover': {
          borderColor: '#98A2B3',
          bgcolor: '#F8FAFC',
        },
        ...sx,
      }}>
      {strings.booking_diary_legend}
    </Button>
  );

  const headerActionButtonSx = {
    width: 'auto',
    minHeight: 36,
    height: 36,
    textTransform: 'capitalize',
    px: 1.5,
  } as const;

  const renderViewToggle = () => (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <ToggleButtonGroup
        exclusive
        size="small"
        value={viewType === VENUE_AVAILABILITY ? null : viewType}
        onChange={(_event, nextView) => {
          if (nextView) changeViewType(nextView)();
        }}
        aria-label="calendar view"
        sx={{
          flexShrink: 0,
          bgcolor: '#EEF2F6',
          borderRadius: '8px',
          p: '2px',
          '& .MuiToggleButtonGroup-grouped': {
            border: 0,
            borderRadius: '6px !important',
            m: 0,
          },
          '& .MuiToggleButton-root': {
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.8125rem',
            px: 1.25,
            py: 0.25,
            minHeight: 28,
            minWidth: screenSizeDownMd ? 36 : 56,
            color: '#64748B',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.65)' },
            '&.Mui-selected': {
              bgcolor: '#fff',
              color: '#334155',
              fontWeight: 600,
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
              '&:hover': { bgcolor: '#fff' },
            },
          },
        }}>
        <ToggleButton value={Views.MONTH}>
          {screenSizeDownMd ? strings.monthText.substring(0, 1) : strings.monthText}
        </ToggleButton>
        <ToggleButton value={Views.WEEK}>
          {screenSizeDownMd ? strings.weekText.substring(0, 1) : strings.weekText}
        </ToggleButton>
        <ToggleButton value={Views.DAY}>
          {screenSizeDownMd ? strings.dayText.substring(0, 1) : strings.dayText}
        </ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title={strings.venueAvailabilityText}>
        <IconButton
          size="small"
          aria-label={strings.venueAvailabilityText}
          aria-pressed={viewType === VENUE_AVAILABILITY}
          onClick={() => changeViewType(VENUE_AVAILABILITY)()}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            color: viewType === VENUE_AVAILABILITY ? '#334155' : '#64748B',
            bgcolor: viewType === VENUE_AVAILABILITY ? '#fff' : '#EEF2F6',
            boxShadow: viewType === VENUE_AVAILABILITY ? '0 1px 2px rgba(15, 23, 42, 0.08)' : 'none',
            '&:hover': { bgcolor: viewType === VENUE_AVAILABILITY ? '#fff' : '#E4E7EC' },
          }}>
          <GridOnOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  return (
    <Typography component="div" width={modal ? '100%' : 'auto'} className="bookings-container">
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      {isLoading && <PageLoader />}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
          columnGap: 1,
          mb: 1,
          minWidth: 0,
        }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ justifySelf: 'start', flexShrink: 0 }}>
          {!hideNewBookingButton && userPrivileges.includes(privileges.createBooking) ? (
            screenSizeDownSm ? (
              <PrimaryButton
                variant="contained"
                fullWidth={false}
                onClick={() => navigate(routes.addBooking)}
                sx={{ minWidth: 36, width: 36, height: 36, px: 0 }}>
                <AddIcon fontSize="small" />
              </PrimaryButton>
            ) : (
              <PrimaryButton
                variant="contained"
                fullWidth={false}
                onClick={() => navigate(routes.addBooking)}
                sx={headerActionButtonSx}>
                <AddIcon fontSize="small" />
                {strings.new_booking_title}
              </PrimaryButton>
            )
          ) : null}
          <Tooltip title={strings.customermgmt_button_export || 'Export'}>
            <IconButton
              color="primary"
              aria-label={strings.customermgmt_button_export || 'Export'}
              onClick={event => setExportMenuAnchor(event.currentTarget)}
              sx={{
                width: 36,
                height: 36,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: 1,
                ...(exportMenuAnchor ? { bgcolor: `${theme.palette.primary.main}14` } : {}),
              }}>
              <FileUploadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={() => setExportMenuAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: { mt: 0.5, minWidth: 240, borderRadius: '10px', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)' },
              },
            }}>
            <MenuItem onClick={handleExportMonthSummary}>
              {strings.booking_diary_export_summary_month}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setExportMenuAnchor(null);
                setIsExportReport(true);
              }}>
              {strings.booking_diary_export_booking_for_date}
            </MenuItem>
            <MenuItem onClick={handleExportCoverReport}>
              {strings.reports_button_export_cover_report}
            </MenuItem>
            <MenuItem onClick={handleExportSettlementReport}>
              {strings.reports_button_export_settlement}
            </MenuItem>
          </Menu>
          <Tooltip title={strings.filter_text}>
            <IconButton
              color="primary"
              aria-label={strings.filter_text}
              onClick={() => setShowFilters(open => !open)}
              sx={{
                width: 36,
                height: 36,
                border: '1px solid',
                borderColor: theme.palette.primary.main,
                borderRadius: 1,
                ...(showFilters || diarySearch.trim() || formData?.includeCancelled
                  ? { bgcolor: `${theme.palette.primary.main}14` }
                  : {}),
              }}>
              {showFilters || diarySearch.trim() || formData?.includeCancelled ? (
                <FilterAltIcon fontSize="small" />
              ) : (
                <FilterAltOutlinedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </Stack>
        {renderCompactDateNav()}
        <Box sx={{ justifySelf: 'end' }}>{renderViewToggle()}</Box>
      </Box>
      {showFilters && (
        <Box
          sx={{
            width: '100%',
            px: 1.5,
            py: 1.25,
            mb: 1,
            borderRadius: '10px',
            bgcolor: '#F8FAFC',
            border: '1px solid',
            borderColor: '#EEF2F6',
          }}>
          {renderDiarySearchRow()}
        </Box>
      )}
      {hasActiveFilterChips && (
        <Box sx={{ mb: 0.75, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {activeFilterChips}
        </Box>
      )}
      {screenSizeDownBookingsMobile && (
        <Box className="booking-mobile-status-row" sx={{ mb: 0.75 }}>
          <Box className="booking-mobile-scroll booking-mobile-status-pills">{statusFilterPills}</Box>
          {viewType !== VENUE_AVAILABILITY && renderChipTitleToggle()}
        </Box>
      )}
      {!screenSizeDownMd && (
        <Box display="flex" alignItems="center" flexWrap="wrap" width="100%" sx={{ mb: 0.75, gap: 0.5 }}>
          <Box display="flex" flexWrap="wrap" alignItems="center" flex={1} minWidth={0} sx={{ gap: 0.5 }}>
            {statusFilterPillsDesktop}
          </Box>
          {viewType !== VENUE_AVAILABILITY && renderChipTitleToggle({ ml: 'auto' })}
        </Box>
      )}
      <Typography className={`calendar-container ${viewType}`}>
        <Grid container>
          <Grid item xs={12}>
            {viewType === VENUE_AVAILABILITY ? (
              <VenueAvailabilityGrid
                monthDate={initialDate || dayjs().format('YYYY-MM-DD')}
                venues={venueList}
                partsOfDay={partOfDays}
                bookings={bookingsData}
                strings={strings}
                selectedVenueId={selectedVenueId}
                onSelectedVenueChange={setSelectedVenueId}
                onDateOverlayClick={(day, kind) => {
                  const selectedDate = day.format('DD-MM-YYYY');
                  setIsLoading(true);
                  handleSelectSlotHelper(
                    selectedDate,
                    { selectedDate, monthEndDate: day.endOf('month').format('DD-MM-YYYY') },
                    kind === 'unavailable' ? UNAVAILABLE : HIGH_DEMAND,
                  );
                }}
              />
            ) : (
            <FullCalendar
              slotLabelFormat={
                viewType === Views.DAY
                  ? [
                      {
                        hour: 'numeric',
                        minute: '2-digit',
                        omitZeroMinute: false,
                        meridiem: false,
                        hour12: false,
                      }, // lower level of text
                    ]
                  : [
                      {
                        day: '2-digit',
                        weekday: 'short',
                        separator: '/',
                        month: '2-digit',
                        omitCommas: true,
                      }, // top level of text
                      {
                        hour: 'numeric',
                        minute: 'numeric',
                        omitZeroMinute: false,
                        meridiem: false,
                        hour12: false,
                      }, // lower level of text
                    ]
              }
              locales={allLocales}
              locale={calendarLocale || 'en'}
              dayMaxEvents={1}
              moreLinkContent={args => {
                return `${args.shortText} ${strings.more}`;
              }}
              height={'520px'}
              ref={calendarRef}
              schedulerLicenseKey={'0939332011-fcs-1717039343'}
              plugins={[resourceTimelinePlugin, dayGridPlugin, interactionPlugin, timegridPlugin]}
              headerToolbar={false}
              resources={calendarResources}
              resourceOrder={'sortOrder'}
              resourceAreaHeaderContent={() =>
                viewType === Views.WEEK || viewType === Views.DAY ? (
                  <VenueFilterSelect
                    venues={venueFilterOptions}
                    value={selectedVenueId}
                    allLabel={strings.venue_availability_all_venues}
                    onChange={setSelectedVenueId}
                  />
                ) : (
                  strings.venues
                )
              }
              resourceAreaWidth={220}
              initialView={getInitialView(viewType)}
              views={{
                resourceTimelineDay: {
                  scrollTime: '06:00:00',
                  eventMinHeight: 44,
                  slotDuration: '01:00:00',
                },
                resourceTimelineWeek: {
                  scrollTime: '06:00:00',
                  eventMinHeight: 44,
                },
              }}
              datesSet={arg => {
                if (arg.view.type !== 'resourceTimelineDay' && arg.view.type !== 'resourceTimelineWeek') {
                  dayScrollKeyRef.current = '';
                  return;
                }
                const key = String(arg.view.currentStart?.valueOf?.() ?? arg.start);
                if (dayScrollKeyRef.current === key) {
                  return;
                }
                dayScrollKeyRef.current = key;
                window.setTimeout(() => arg.view.calendar.scrollToTime('06:00:00'), 0);
              }}
              selectable={true}
              events={calendarEvents}
              select={handleSelectSlot}
              dateClick={info => {
                if (viewType !== Views.DAY && viewType !== Views.WEEK) return;
                openCellDrawer(info.date, cellClickStatus(info.date, info.resource?.id));
              }}
              eventContent={renderEventContent}
              eventClick={handleNavigateToBookingDetail as any}
              initialDate={initialDate}
              slotLaneClassNames={arg => {
                if (viewType !== Views.DAY && viewType !== Views.WEEK) return [];
                const highDemand = bookingsData.some(
                  (booking: any) =>
                    booking.status === HIGH_DEMAND && isDateInRange(arg.date, booking.start, booking.end),
                );
                return highDemand ? ['high-demand-slot'] : [];
              }}
              dayCellContent={A}
              dayCellDidMount={handleDayCellDidMount}
              dayCellWillUnmount={handleDayCellWillUnmount}
              slotLabelContent={A}
            />
            )}
          </Grid>
        </Grid>
        {screenSizeDownMd && !screenSizeDownBookingsMobile && (
          <Box display="flex" alignItems="center" flexWrap="wrap" width="100%" my={0.5}>
            <Box display="flex" flexWrap="wrap" alignItems="center" flex={1} minWidth={0}>
              {statusFilterPillsDesktop}
            </Box>
            {viewType !== VENUE_AVAILABILITY && renderChipTitleToggle({ mb: 1, ml: 'auto' })}
          </Box>
        )}
        {viewType !== VENUE_AVAILABILITY && (
          <Box sx={{ mt: 1, display: 'flex' }}>{renderLegendButton({ ml: 0 })}</Box>
        )}
        {isClose && (
          <DrawerMenu
            closeButton={true}
            flexColumn
            overlapHeader
            width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
            open={isClose}
            handleClose={() => {
              setIsClose(false);
            }}
            title={`${strings.details_on_text}  ${currentDate}`}>
            <Box p={1} width="100%">
              <BookingTableInfo item={modalDates ? modalDates : []} focusSection={focusSection} />
            </Box>
          </DrawerMenu>
        )}
        <DrawerMenu
          closeButton={true}
          flexColumn
          overlapHeader
          width={getDrawerWidth(screenSizeDownSm, screenSizeDownMd)}
          open={isExportReport}
          handleClose={() => {
            setIsExportReport(false);
          }}
          title={strings.booking_diary_export_reports}>
          <Box display="flex" flexDirection="column" alignItems="flex-end">
            <Typography mt={2} width="100%">
              <DateInput
                label={strings.booking_diary_export_reports}
                value={exportDate ? dayjs(exportDate) : null}
                onChange={value => setExportDate(dayjs(value).format('YYYY-MM-DD'))}
                textChange={value => setExportDate(dayjs(value).format('YYYY-MM-DD'))}
              />
              <Button
                disabled={isLoading}
                onClick={handleExportReport}
                sx={{ mt: 2, mr: 1 }}
                variant="contained"
                endIcon={<FileDownloadIcon />}>
                {strings.booking_diary_export_reports}
              </Button>
              {canExportAlif && (
                <>
                  <Button
                    disabled={isLoading}
                    onClick={() => handleAlifExport('rv')}
                    sx={{ mt: 2, mr: 1 }}
                    variant="contained"
                    endIcon={<FileDownloadIcon />}>
                    {strings.booking_diary_alif_export_rv}
                  </Button>
                  <Button
                    disabled={isLoading}
                    onClick={() => handleAlifExport('cl')}
                    sx={{ mt: 2 }}
                    variant="contained"
                    endIcon={<FileDownloadIcon />}>
                    {strings.booking_diary_alif_export_cl}
                  </Button>
                </>
              )}
            </Typography>
          </Box>
        </DrawerMenu>
        <LegendInfo
          open={isLegendOpen}
          handleClose={() => setIsLegendOpen(false)}
          calendarFlag={calendarFlag}
          strings={strings}
        />
      </Typography>
    </Typography>
  );
};
